import { useState } from "react";
import toast from "react-hot-toast";
import PageHeader from "../../components/layout/PageHeader";
import { Card } from "../../components/ui/Card";
import { configApi } from "../../api/config";
import { adminApi } from "../../api/admin";
import { useConfig } from "../../context/ConfigContext";
import { useAuth } from "../../hooks/useAuth";

export default function DeveloperTools() {
  const { config, refresh } = useConfig();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [debugSaving, setDebugSaving] = useState(false);
  const [debugEnabled, setDebugEnabled] = useState(config["debug.mode"] === "true");
  const [testEmailTo, setTestEmailTo] = useState(user?.email ?? "");
  const [testEmailSending, setTestEmailSending] = useState(false);

  const toggleDebugMode = async () => {
    const newValue = !debugEnabled;
    setDebugEnabled(newValue);
    setDebugSaving(true);
    try {
      await configApi.update({ "debug.mode": newValue ? "true" : "false" });
      toast.success(`Debug mode ${newValue ? "enabled" : "disabled"}`);
      await refresh();
    } catch (err) {
      setDebugEnabled(!newValue);
      toast.error(err instanceof Error ? err.message : "Failed to update debug mode");
    } finally {
      setDebugSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setTestEmailSending(true);
    try {
      const result = await adminApi.testEmail(testEmailTo);
      if (result.smtp) {
        toast.success(`Test email sent to ${testEmailTo}`);
      } else {
        toast.success("No SMTP configured — email logged to console instead");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send test email");
    } finally {
      setTestEmailSending(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <div>
      <PageHeader
        title="Developer Tools"
        subtitle="Diagnostic settings — not for everyday use"
      />

      <div className="p-6 lg:p-8">
        <div className="mb-6 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <span>⚠</span>
          These settings are intended for diagnosing issues. Changes here affect all admin and director users.
        </div>

        <Card className="border border-amber-200 bg-amber-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-stone-700">Debug Mode</p>
              <p className="text-xs text-stone-500 mt-0.5">
                Show a diagnostic panel on the Dashboard for admin and director users.
              </p>
            </div>
            <button
              type="button"
              onClick={toggleDebugMode}
              disabled={debugSaving}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-50 ${
                debugEnabled ? "bg-amber-500" : "bg-stone-200"
              }`}
              aria-pressed={debugEnabled}
              aria-label="Toggle debug mode"
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                  debugEnabled ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>
        </Card>

        <Card className="mt-4 border border-amber-200 bg-amber-50">
          <div>
            <p className="text-sm font-medium text-stone-700">Send Test Email</p>
            <p className="text-xs text-stone-500 mt-0.5">
              Send a diagnostic email to verify SMTP is configured correctly.
            </p>
            <div className="mt-3 flex gap-2">
              <input
                type="email"
                value={testEmailTo}
                onChange={(e) => setTestEmailTo(e.target.value)}
                placeholder="recipient@example.com"
                className="flex-1 rounded-md border border-stone-300 bg-white px-3 py-1.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <button
                type="button"
                onClick={sendTestEmail}
                disabled={testEmailSending || !testEmailTo}
                className="rounded-md bg-amber-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50"
              >
                {testEmailSending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
